import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import type {
  McpOAuthGrant,
  McpOAuthGrantPreview,
  McpOAuthRedirect,
  McpOAuthRequest,
  McpOAuthRevokeResult,
} from './model'

export type * from './model'

const requestPath = (id: string) => `/mcp/oauth/requests/${encodeURIComponent(id)}`

/**
 * MCP OAuth consent + connected-apps API. None of these endpoints need
 * `x-tenant-id` (grants belong to the user, not to one club); the shared
 * client still sends it when a club is selected, which the backend ignores.
 * "Public" endpoints work without a session; the rest need the Firebase ID
 * token the interceptor attaches (missing → 403, invalid/expired → 401).
 */
export const McpOAuthService = (client: AxiosInstance) => {
  return {
    /** GET /mcp/oauth/requests/:id (public). 404 unknown id, 410 expired / no longer pending. */
    async getRequest(id: string): Promise<McpOAuthRequest> {
      const { data } = await wrapApiCall(() => client.get<McpOAuthRequest>(requestPath(id)))
      return data
    },

    /** GET /mcp/oauth/requests/:id/grant-preview — the request plus every club the user can grant. */
    async getGrantPreview(id: string): Promise<McpOAuthGrantPreview> {
      const { data } = await wrapApiCall(() =>
        client.get<McpOAuthGrantPreview>(`${requestPath(id)}/grant-preview`)
      )
      return data
    },

    /**
     * POST /mcp/oauth/requests/:id/approve ("Allow").
     *
     * Pass `undefined` to grant every club, including clubs gained later.
     * Anything else is sent as-is — but note the API also records a list that
     * covers every manageable club as an all-clubs grant, so the consent page
     * must disclose that either way. An empty array is rejected with 400
     * rather than silently widened. 409/410 = already decided or expired.
     */
    async approveRequest(id: string, clubIds?: string[]): Promise<McpOAuthRedirect> {
      const { data } = await wrapApiCall(() =>
        client.post<McpOAuthRedirect>(
          `${requestPath(id)}/approve`,
          clubIds === undefined ? {} : { clubIds }
        )
      )
      return data
    },

    /** POST /mcp/oauth/requests/:id/deny ("Deny", public, idempotent). */
    async denyRequest(id: string): Promise<McpOAuthRedirect> {
      const { data } = await wrapApiCall(() =>
        client.post<McpOAuthRedirect>(`${requestPath(id)}/deny`)
      )
      return data
    },

    /** GET /mcp/oauth/grants — the caller's connected AI apps, newest first. */
    async listGrants(): Promise<McpOAuthGrant[]> {
      const { data } = await wrapApiCall(() => client.get<McpOAuthGrant[]>('/mcp/oauth/grants'))
      return data ?? []
    },

    /** DELETE /mcp/oauth/grants/:id — disconnect immediately. 404 if it is not yours. */
    async revokeGrant(id: string): Promise<McpOAuthRevokeResult> {
      const { data } = await wrapApiCall(() =>
        client.delete<McpOAuthRevokeResult>(`/mcp/oauth/grants/${encodeURIComponent(id)}`)
      )
      return data
    },
  }
}
